export const jobSortTypes = {
  DEFAULT: 0,
  COMPANY_NAME_ASCENDING: 1,
  COMPANY_NAME_DESCENDING: 2,
  JOB_UPDATED_ASCENDING: 3,
  JOB_UPDATED_DESCENDING: 4,
  JOB_PUBLISHED_ASCENDING: 5,
  JOB_PUBLISHED_DESCENDING: 6,
  JOB_CREATED_ASCENDING: 7,
  JOB_CREATED_DESCENDING: 8,
} as const
export type JobSortTypes = (typeof jobSortTypes)[keyof typeof jobSortTypes]

export const jobEditStatus = {
  LOCKED: 1,
  UNLOCKED: 2,
  MEMBER_LOCKED: 3,
  ADMIN_LOCKED: 4,
} as const
export type JobEditStatus = (typeof jobEditStatus)[keyof typeof jobEditStatus]
export type JobEditStatusSettable =
  | typeof jobEditStatus.LOCKED
  | typeof jobEditStatus.UNLOCKED

export const jobExtensionFlg = {
  NO_EXTEND: 0,
  EXTEND: 1,
} as const
export type JobExtensionFlg =
  (typeof jobExtensionFlg)[keyof typeof jobExtensionFlg]

export const jobLocationTypes = {
  OFFICE: 'OFFICE',
  OFFICE_AND_REMOTE: 'OFFICE_AND_REMOTE',
  FULL_REMOTE: 'FULL_REMOTE',
} as const
export type JobLocationType =
  (typeof jobLocationTypes)[keyof typeof jobLocationTypes]

export const jobPublicFlg = {
  PROVISIONAL: 0,
  DRAFT: 1,
  PUBLISH: 2,
  STOP: 4,
  UNDER_REVIEW: 5,
  NEED_TO_FIX: 6,
} as const
export type JobPublicFlg = (typeof jobPublicFlg)[keyof typeof jobPublicFlg]

export interface Occupation {
  occupationId: number
  occupationName: string
}

export interface Employment {
  employmentId: number
  employmentName: string
}

export interface JobSkillLevel {
  levelId: number
  levelName: string
}

export interface BusinessForm {
  formId: number
  formName: string
}

export interface CompanySize {
  sizeId: number
  sizeName: string
}

export interface JobListInfo {
  jobCode: string
  jobName: string
  companyName: string
  companyCode: string
  companyEnabledServices: {
    agent: boolean
    scout: boolean
  }
  employments: Employment[]
  occupations: Occupation[]
  skillLevels: JobSkillLevel[]
  businessForms: BusinessForm[]
  companySizes: CompanySize[]
  managementNote: string
  publicationStartAt: string
  publicFlg: JobPublicFlg
  adviserEmployeeName: string
  adviserEmployeeCode: string
  isWebPublished: boolean
  services: {
    agent: boolean
    scout: boolean
  }
}

export interface JobLocation {
  postalCode: string
  prefecture: number
  addressCity: string
  addressLocal: string
}

export interface JobAdditionalInfo {
  jobCode: string
  jobAttraction: string
  occupations: Occupation[]
  skillLevels: JobSkillLevel[]
  businessForm: BusinessForm
  companySize: CompanySize
  tags: Tag[]
  managementNote: string
}

export interface JobRelatedLinkInfo {
  title: string
  text: string
  linkUrl: string
  imageUrl: string
}

export interface JobInfo {
  jobCode: string
  companyName: string
  companyCode: string
  companyEnabledServices: {
    agent: boolean
    scout: boolean
  }
  jobName: string
  occupation: string
  employments: Employment[]
  tags: Tag[]
  workingHours: string
  coverImagePath: string
  jobDescription: string
  welcomeCondition: string
  jobLocationType: JobLocationType
  jobLocations: JobLocation[]
  salary: string
  benefits: string
  holiday: string
  trialPeriod: string
  hiringStep: string
  organizationImagePath: string
  organizationSupplement: string
  teamImagePath: string
  teamSupplement: string
  departmentMission: string
  designerEmployees: string
  jobRole: string
  teamFeature: string
  tool: string
  productTask: string
  remarks: string
  companyThinking: string
  evaluation: string
  training: string
  careerPath: string
  successDesigner: string
  bossExpect: string
  designerImpressions: string
  publicationStartAt?: string
  extensionFlg: JobExtensionFlg
  publicFlg: JobPublicFlg
  isWebPublished: boolean
  services: {
    agent: boolean
    scout: boolean
  }
  relatedLinks: JobRelatedLinkInfo[]
  isArchive: boolean
}

export interface JobPreviewInfo {
  jobName: string
  companyCode: string
  companyName: string
  companyLogoImagePath: string
  companyEnabledServices: {
    agent: boolean
    scout: boolean
  }
  occupation: string
  employments: Employment[]
  tags: Tag[]
  workingHours: string
  coverImagePath: string
  jobDescription: string
  welcomeCondition: string
  jobLocationType: JobLocationType
  jobLocations: JobLocation[]
  salary: string
  benefits: string
  holiday: string
  trialPeriod: string
  hiringStep: string
  organizationImagePath: string
  organizationSupplement: string
  teamImagePath: string
  teamSupplement: string
  departmentMission: string
  designerEmployees: string
  jobRole: string
  teamFeature: string
  tool: string
  productTask: string
  remarks: string
  companyThinking: string
  evaluation: string
  training: string
  careerPath: string
  successDesigner: string
  bossExpect: string
  designerImpressions: string
  jobAttraction: string
  publicationStartAt: string
  publicFlg: JobPublicFlg
  isWebPublished: boolean
  services: {
    agent: boolean
    scout: boolean
  }
  relatedLinks: JobRelatedLinkInfo[]
  isArchive: boolean
  updatedAt: string
}

export interface JobEditStatusInfo {
  jobCode: string
  editStatus: JobEditStatus
  editorName: string
  editorProfileImagePath: string
}

export interface JobRevisionComment {
  jobCode: string
  revisionOutline: string
  revisionDetail: string
  fromEmployeeCode: string
  fromEmployeeName: string
  fromEmployeeIconPath: string
}

export interface CreateJobArgs {
  companyCode: string
}

export interface CreateJobResult extends BaseApiResult {
  jobCode: string
}

export interface GetJobsArgs {
  num: number
  page: number
  publicFlg?: JobPublicFlg
  employments?: string
  tags?: string
  occupations?: string
  keyword?: string
  adviserEmployeeCode?: string
  skillLevels?: string
  businessForms?: string
  companySizes?: string
  sort?: JobSortTypes
}
export interface GetJobsResult extends BaseApiResult {
  totalCount: number
  jobs: JobListInfo[]
}

export interface GetArchivedJobsArgs {
  num: number
  page: number
}
export interface GetArchivedJobsResult extends BaseApiResult {
  totalCount: number
  jobs: JobListInfo[]
}
export interface UpdateJobArchiveFlgArgs {
  jobCode: string
  jobArchiveFlg: boolean
}

export interface GetJobInfoResult extends BaseApiResult {
  jobInfo: JobInfo
}

export interface GetJobPreviewInfoResult extends BaseApiResult {
  jobPreviewInfo: JobPreviewInfo
}

export interface GetJobAdditionalInfoResult extends BaseApiResult {
  jobAdditionalInfo: JobAdditionalInfo
}

export interface UpdateJobPublicFlgArgs {
  jobCode: string
  publicFlg: JobPublicFlg
}

export interface UpdateJobEditStatusArgs {
  jobCode: string
  editStatus: JobEditStatusSettable
}

export interface UpdateJobManagementNoteArgs {
  jobCode: string
  managementNote: string
}

export interface UpdateJobEditStatusResult extends BaseApiResult {
  jobEditStatusInfo: JobEditStatusInfo
}

export interface GetJobRevisionCommentResult extends BaseApiResult {
  revisionComment: JobRevisionComment
}

export const jobStatusTransitionMap: {
  [key in JobPublicFlg]: JobPublicFlg[]
} = {
  [jobPublicFlg.PROVISIONAL]: [],
  [jobPublicFlg.DRAFT]: [jobPublicFlg.DRAFT],
  [jobPublicFlg.UNDER_REVIEW]: [jobPublicFlg.UNDER_REVIEW],
  [jobPublicFlg.NEED_TO_FIX]: [jobPublicFlg.NEED_TO_FIX],
  [jobPublicFlg.PUBLISH]: [jobPublicFlg.PUBLISH, jobPublicFlg.STOP],
  [jobPublicFlg.STOP]: [jobPublicFlg.PUBLISH, jobPublicFlg.STOP],
}

export const jobStatusListItems: {
  [key in JobPublicFlg]: {
    value: JobPublicFlg
    label: string
    circleFill?: string
    circleStroke?: string
    tagColor?: TagColor
  }
} = {
  [jobPublicFlg.PROVISIONAL]: {
    value: jobPublicFlg.PROVISIONAL,
    label: '下書き',
    circleStroke: '#BDBDBD',
    tagColor: 'blue-grey-dark',
  },
  [jobPublicFlg.DRAFT]: {
    value: jobPublicFlg.DRAFT,
    label: '下書き',
    circleStroke: '#BDBDBD',
  },
  [jobPublicFlg.UNDER_REVIEW]: {
    value: jobPublicFlg.UNDER_REVIEW,
    label: '確認中',
    circleFill: '#F09819',
  },
  [jobPublicFlg.NEED_TO_FIX]: {
    value: jobPublicFlg.NEED_TO_FIX,
    label: '要修正',
    circleFill: '#D62022',
  },
  [jobPublicFlg.PUBLISH]: {
    value: jobPublicFlg.PUBLISH,
    label: '募集中',
    circleFill: '#1B97F0',
  },
  [jobPublicFlg.STOP]: {
    value: jobPublicFlg.STOP,
    label: '募集終了',
    circleFill: '#979797',
  },
}

export const dummyJob: any = {
  jobName: 'フロントエンドエンジニア',
  occupation: '',
  employments: [
    {
      employmentId: 1,
      employmentName: '正社員',
    },
  ],
  workingHours: '9：00～18：00【フレックス制】',
  coverImagePath: '/job/CAAAAAAAAA/C_image_file_1610698863.jpg',
  jobDescription:
    '「世界の経営指標を変える」このビジョンを実現するために、モチベーションクラウドの開発をお願いします。組織だからこそ得られる、かけがえのない幸せにより、世界中の人々をもっと笑顔にしたいと思っています。社会、働く人々に貢献できる価値を高めるために、ともに挑戦する仲間を探しています。このプロダクトは従業員の方が、本音で自社や自分のことを入力してもらうことが最重要です。\nフロントエンドの開発は、UI／UXデザイナー、プロダクトマネジャーとともに顧客の声を分析しながら進めております。このチームの一員として顧客体験の向上にコミットしていただきます。\nモチベーションクラウドはモデリングの難しい「組織」の表現や、多岐にわたる質問項目をお客様にストレスなく入力していただくことを始めとして、B2B SaaSでありながら 2C レベルのUI／UXが求められるプロダクトであると考えています。',
  welcomeCondition:
    '■求めるマインド ・デザインの意図を語り、言語化することを大切だと理解している ・役割に閉じずに、領域を超えることを厭わない ・チームワークを大切だと考え、個人の成⻑だけでなくチームの成⻑に貢献する ・最新のプロダクト、業界の動向、デザインの情報収集に余念がない ・見た目や美しさだけでなく、ビジネス的な価値にこだわる ・デザイナーとしてあらゆる歴史や文化を尊重する ・クライアントパートナーの事業を深く理解し、コミットする姿勢\n・HCD (人間中心設計)を大事にしている\n■求めるスキル・経験\n・企業やサービスのブランドデザイン経験(新規またはリニューアル)\n・最新のビジュアルトレンドとガイドライン(Human Interface Guidelines、Material Design)に沿い、デジタルインターフェイス(スマート フォンアプリケーション、Webアプリケーションアプリのいずれか)のビジュアルUIのデザイン経験 ・事業のVision/Missionからブランドの思想を言語化し、CI / VIに反映できる\n・クリエイティブの意図を言語化し、論理的に説明できる\n・Sketch・Figma・XDなどを使用したデジタルプロダクトのデザイン経験\n・Photoshop・Illustratorなどを使用したグラフィックデザイン経験\n・アートディレクション経験のある方\n・エンジニアリングへの理解(Web、App、バックエンド) ・クライアント(自社サービスの場合は社内スタッフも含む)への企画書、提案書作成、プレゼンテーション業務\n■歓迎スキル・経験 ・コピーライティングの知識がある方(宣伝会議コピーライター養成講座など) ・イベント登壇経験\n・デザイナーとしてのサービスの立ち上げ、運営経験 ・0→1のブランド立ち上げ・運用経験のある方 ・メンバー育成・マネージメント経験のある方\n・マーケティングの知識がある方',
  jobLocationType: jobLocationTypes.OFFICE_AND_REMOTE,
  jobLocations: [
    {
      postalCode: '3510011',
      prefecture: 1,
      addressCity: 'XX市YY区',
      addressLocal: '以降の住所が続く',
    },
    {
      postalCode: '1040061',
      prefecture: 13,
      addressCity: '中央区 ',
      addressLocal: '銀座6-10-1',
    },
  ],
  salary:
    '年収500万円〜\nこの行は改行されているかの確認用。\n\nこの行は改行されているかの確認用。',
  benefits:
    '・3ヶ月毎にフォーカスを決めて開発目標を設定しています・チームごとに1〜2週間のスプリントでアジャイル開発（Scrum）を行っています\n・職種によりMacBook Pro 13または16インチ を支給しています\n・別途、外部ディスプレイを希望者に支給しています\n・エディタやIDE、その他開発ツールは自由です\n・22時完全消灯\n・月に一回ほど、社内会議室を利用して銀座Railsを開催しています',
  holiday:
    '・休暇：有給休暇 / 夏季休暇 / 年末年始休暇 / 慶弔休暇 / 産休・育休 / 週休2日制 / 土日祝日休み / 年間休日120日以上・福利厚生制度：交通費全額支給 / 社会保険完備 / 持株会制度 / 雇用保険 / 労災保険 / 健康保険 / 厚生年金保険 / 社員持株会制度',
  trialPeriod: '試用期間：6ヶ月（条件等は変わりません）',
  hiringStep:
    '書類選考→1次面接(デザインディレクター)→2次面接(人事)→3次面接(リードデザイナー)→最終面接(CEO、GM)→内定 ※選考内容・回数は変動がございます。カジュアル面談からの実施も可能です。最終面接前にリファレンスチェックがございます。',
  organizationImagePath: '/job/CAAAAAAAAA/O_image_file_1610698873.jpg',
  organizationSupplement:
    '全組織図（デザイナーの立ち位置）の補足テキストです。\nこの行は改行されているかの確認用。\n\nこの行は改行されているかの確認用。',
  teamImagePath: '/job/CAAAAAAAAA/T_image_file_1610698880.jpg',
  teamSupplement:
    'チーム体制図の補足テキストです。\nこの行は改行されているかの確認用。\n\nこの行は改行されているかの確認用。',
  departmentMission:
    'モチベーションエンジニアリングですべての組織を変えることにフォーカスする。\nこの行は改行されているかの確認用。\n\nこの行は改行されているかの確認用。',
  designerEmployees:
    '111人\nこの行は改行されているかの確認用。\n\nこの行は改行されているかの確認用。',
  jobRole:
    '・モチベーションクラウドのフロントエンドの設計、開発、計測\n・高速に動作して使いやすく、再利用可能なUIコンポーネントライブラリの開発\n・StoryBook, cypress, webpackなどを利用したフロントエンド開発基盤の構築や改善',
  teamFeature:
    '内製化し始めてから約2年と短く、スタートアップ企業のような雰囲気があり、限定した役割だけではなく、ご自身の希望により幅広い役割を担うことができます。\nこれから、開発組織の制度・風土を一緒につくって行くことが可能です。',
  tool: '▼開発環境 ・サーバーサイド要素技術 \n　- Ruby 2.4系 \n　- Ruby on Rails 5.1系\n・フロントエンド要素技術 \n　- Sass \n　- Vue 2.5.x系\n　- webpack\n　- storybook\n　- cypress\n　- jest\n・インフラ要素技術 \n　- AWS (Elastic Beanstalk,EC2,Aurora...)\n・開発環境\n　- Docker\n・その他 \n　- Git \n　- Confluence \n　- CircleCI \n　- Datadog \n　- Sentry \n　- Slack',
  productTask:
    '現在は特に、大手企業様にもご安心してご利用いただく体験提供、E2Eの継続的インテグレーション環境の構築を重点項目として取り組んでいます。\n経営として、新たな事業変革期と認識しており、ソフトウェアエンジニアの採用強化と環境整備を今まさに作り上げている最中です。',
  remarks: '備考欄のテキストです。\n備考は特にありません。',
  companyThinking:
    '「デザインの力を証明する」というミッションの通り、社会全体に対してデザインの価値を伝えていくべく。\nデザインの可能性を信じている共通言語を持った社員が集まっています。経営陣のデザインへの情熱は日本一です。',
  evaluation:
    '職務レイヤーをグレード制度で定義しており、半期ごとにグレードに沿った評価基準を適用し、実施します。\n評価自体は、自己評価・上司評価・360度評価をインプットとして行います。',
  training:
    '所属するUnit内でのプロジェクト共有/Division全体でナレッジを共有/Design Leadとの1on1\nシニアメンバーによるメンタリング/サイドプロジェクト/個人の志向に応じた外部研修',
  careerPath:
    '実績、能力に応じたキャリアグレード制度を適用しています。\n\nEx)ジュニアデザイナー ▷ ミドルデザイナー ▷ シニアデザイナー ▷ デザインリード ▷ デザインディレクター',
  successDesigner:
    'アプリの経験があり、下記、グッドパッチのVALUEを体現するメンバーが活躍しています。\n・WHYが人を動かす:Inspire with Why\n・領域を超えよう:Go Beyond\n・最高のチームのつくり手になる:Play as a Team\n・こだわりと遊び心を持つ:Craft Details, Create Delight\n・良いデザインを良いビジネスにする:Good Design Equals Good Business',
  bossExpect:
    '上司がデザイナーに期待することのテキスト内容です。\nこの行は改行されているかの確認用。\n\nこの行は改行されているかの確認用。',
  designerImpressions:
    '今いるデザイナーの声のテキスト内容です。\nこの行は改行されているかの確認用。\n\nこの行は改行されているかの確認用。',
  publicationStartAt: '2017-01-15T21:00:00+09:00',
}

export const jobLocationTypeItems = [
  { value: jobLocationTypes.OFFICE, label: '出社勤務' },
  {
    value: jobLocationTypes.OFFICE_AND_REMOTE,
    label: '在宅勤務（一部出社あり）',
  },
  { value: jobLocationTypes.FULL_REMOTE, label: '在宅勤務（フルリモート）' },
]
